<template>
  <div class="flex h-screen bg-background overflow-hidden">

    <!-- ── Mobile backdrop ───────────────────────────────────── -->
    <Transition name="fade">
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 z-40 bg-black/60 lg:hidden"
        @click="sidebarOpen = false"
      />
    </Transition>

    <!-- ── Sidebar ───────────────────────────────────────────── -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar text-sidebar-foreground transition-all duration-300 ease-in-out',
        'lg:relative lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        collapsed ? 'w-16' : 'w-64',
      ]"
    >
      <!-- Logo -->
      <div class="flex h-14 items-center border-b border-sidebar-border px-4 shrink-0">
        <div class="flex items-center gap-3 overflow-hidden">
          <img :src="logoMark" alt="ExoTrack" class="h-8 w-8 shrink-0" />
          <Transition name="fade">
            <div v-if="!collapsed" class="min-w-0">
              <p class="text-sm font-bold font-display text-sidebar-foreground leading-none">ExoTrack</p>
              <p class="text-xs text-sidebar-muted-foreground mt-0.5">Admin Portal</p>
            </div>
          </Transition>
        </div>
        <button
          class="ml-auto hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          @click="collapsed = !collapsed"
        >
          <ChevronLeft :class="['h-4 w-4 transition-transform duration-200', collapsed ? 'rotate-180' : '']" />
        </button>
      </div>

      <!-- Nav -->
      <nav class="flex-1 overflow-y-auto scrollbar-hide py-3 px-2 space-y-5">
        <!-- Main groups -->
        <template v-for="(group, gi) in navigationGroups" :key="group.label">
          <div>
            <p
              v-if="!collapsed"
              class="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted-foreground"
            >
              {{ group.label }}
            </p>
            <div v-else class="mb-1 flex justify-center">
              <div class="h-px w-6 bg-sidebar-border" />
            </div>
            <div class="space-y-0.5">
              <router-link
                v-for="item in group.items"
                :key="item.name"
                :to="item.href"
                :class="[
                  'group flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                  isActive(item.href)
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-muted',
                  collapsed ? 'justify-center' : '',
                ]"
                :title="collapsed ? item.name : undefined"
                @click="sidebarOpen = false"
              >
                <component :is="item.icon" class="h-4 w-4 shrink-0" />
                <span v-if="!collapsed" class="flex-1 truncate">{{ item.name }}</span>
                <span
                  v-if="!collapsed && item.badge"
                  class="ml-auto rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-bold text-primary"
                >
                  {{ item.badge }}
                </span>
              </router-link>
            </div>
          </div>
        </template>

        <!-- Configuration -->
        <div>
          <p v-if="!collapsed" class="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted-foreground">
            Configuration
          </p>
          <div v-else class="mb-1 flex justify-center"><div class="h-px w-6 bg-sidebar-border" /></div>
          <div class="space-y-0.5">
            <router-link
              v-for="item in configNavigation"
              :key="item.name"
              :to="item.href"
              :class="[
                'group flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                isActive(item.href)
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-muted',
                collapsed ? 'justify-center' : '',
              ]"
              :title="collapsed ? item.name : undefined"
              @click="sidebarOpen = false"
            >
              <component :is="item.icon" class="h-4 w-4 shrink-0" />
              <span v-if="!collapsed" class="flex-1 truncate">{{ item.name }}</span>
            </router-link>
          </div>
        </div>

        <!-- Tools -->
        <div>
          <p v-if="!collapsed" class="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted-foreground">
            {{ t('nav.configuration') }}
          </p>
          <div v-else class="mb-1 flex justify-center"><div class="h-px w-6 bg-sidebar-border" /></div>
          <div class="space-y-0.5">
            <button
              v-for="action in quickActions"
              :key="action.name"
              :class="[
                'w-full flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-muted transition-colors',
                collapsed ? 'justify-center' : '',
              ]"
              :title="collapsed ? action.name : undefined"
              @click="handleQuickAction(action.action)"
            >
              <component :is="action.icon" class="h-4 w-4 shrink-0" />
              <span v-if="!collapsed">{{ action.name }}</span>
            </button>
          </div>
        </div>
      </nav>

      <!-- User -->
      <div class="shrink-0 border-t border-sidebar-border p-3">
        <button
          :class="['w-full flex items-center gap-3 rounded-md px-2 py-2 hover:bg-sidebar-muted transition-colors text-left', collapsed ? 'justify-center' : '']"
          @click="showProfileMenu = !showProfileMenu"
        >
          <div class="h-8 w-8 shrink-0 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
            {{ userInitials }}
          </div>
          <div v-if="!collapsed" class="flex-1 min-w-0">
            <p class="text-xs font-semibold text-sidebar-foreground truncate">{{ displayName }}</p>
            <p class="text-[10px] text-sidebar-muted-foreground truncate">{{ t('nav.adminPortal') }}</p>
          </div>
          <LogOut v-if="!collapsed" class="h-4 w-4 text-sidebar-muted-foreground hover:text-destructive transition-colors" @click.stop="handleLogout" />
        </button>
      </div>
    </aside>

    <!-- ── Main content ───────────────────────────────────────── -->
    <div class="flex flex-1 flex-col min-w-0 overflow-hidden">

      <!-- Topbar -->
      <header class="h-14 shrink-0 border-b border-border bg-white flex items-center px-4 gap-4">
        <!-- Mobile menu button -->
        <button
          class="lg:hidden h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          @click="sidebarOpen = true"
        >
          <Menu class="h-5 w-5" />
        </button>

        <!-- Page title -->
        <div class="flex-1 min-w-0">
          <h1 class="text-sm font-semibold text-foreground truncate">{{ currentPageTitle }}</h1>
          <div v-if="breadcrumbs.length" class="flex items-center gap-1 text-xs text-muted-foreground">
            <span>Admin</span>
            <ChevronRight class="h-3 w-3" />
            <span class="text-foreground font-medium">{{ breadcrumbs[breadcrumbs.length - 1] }}</span>
          </div>
        </div>

        <!-- Right actions -->
        <div class="flex items-center gap-2">
          <!-- Language toggle -->
          <LangToggle />
          <!-- Status pill -->
          <div class="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200">
            <div class="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span class="text-xs font-medium text-emerald-700">{{ t('auth.allSystemsOperational') }}</span>
          </div>

          <!-- Notifications -->
          <div class="relative">
            <button
              class="relative h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              @click="toggleNotifications"
            >
              <Bell class="h-4 w-4" />
              <span
                v-if="unreadNotifications > 0"
                class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full"
              >
                {{ unreadNotifications > 9 ? '9+' : unreadNotifications }}
              </span>
            </button>

            <Transition name="fade">
              <div
                v-if="showNotifications"
                v-click-outside="() => showNotifications = false"
                class="absolute right-0 mt-1 w-80 rounded-lg border border-border bg-white shadow-lg z-50 animate-fade-in"
              >
                <div class="flex items-center justify-between px-3 py-2 border-b border-border">
                  <p class="text-xs font-semibold text-foreground">{{ t('notifications.title') }}</p>
                  <button
                    class="text-xs text-primary hover:underline"
                    @click="markAllNotificationsRead"
                  >
                    {{ t('notifications.markAllRead') }}
                  </button>
                </div>
                <div class="max-h-80 overflow-y-auto">
                  <p v-if="notifications.length === 0" class="px-3 py-6 text-xs text-muted-foreground text-center">
                    {{ t('notifications.noNotifications') }}
                  </p>
                  <div
                    v-for="n in notifications"
                    :key="n.id"
                    class="px-3 py-2.5 border-b border-border last:border-b-0 cursor-pointer transition-colors hover:bg-accent"
                    :class="{ 'bg-blue-50/60': !n.read }"
                    @click="handleNotificationClick(n)"
                  >
                    <div class="flex items-start gap-2">
                      <div v-if="!n.read" class="h-1.5 w-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-medium text-foreground truncate">{{ n.title }}</p>
                        <p class="text-xs text-muted-foreground mt-0.5 line-clamp-2">{{ n.message }}</p>
                        <p class="text-[10px] text-muted-foreground/70 mt-1">{{ formatNotifTime(n.timestamp) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <!-- User avatar dropdown -->
          <div class="relative">
            <button
              class="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent transition-colors"
              @click="showProfileMenu = !showProfileMenu"
            >
              <div class="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                {{ userInitials }}
              </div>
              <span class="hidden md:block text-xs font-medium text-foreground">{{ displayName }}</span>
              <ChevronDown class="h-3 w-3 text-muted-foreground" />
            </button>

            <Transition name="fade">
              <div
                v-if="showProfileMenu"
                v-click-outside="() => showProfileMenu = false"
                class="absolute right-0 mt-1 w-52 rounded-lg border border-border bg-white shadow-lg py-1 z-50 animate-fade-in"
              >
                <div class="px-3 py-2 border-b border-border mb-1">
                  <p class="text-xs font-semibold text-foreground">{{ displayName }}</p>
                  <p class="text-xs text-muted-foreground truncate">{{ currentUser?.email }}</p>
                </div>
                <router-link
                  to="/admin/profile"
                  class="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                  @click="showProfileMenu = false"
                >
                  <User class="h-4 w-4 text-muted-foreground" />
                  {{ t('profile.title') }}
                </router-link>
                <button
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                  @click="handleSettings"
                >
                  <Settings class="h-4 w-4 text-muted-foreground" />
                  {{ t('settings.title') }}
                </button>
                <div class="border-t border-border my-1" />
                <button
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-red-50 transition-colors"
                  @click="handleLogout"
                >
                  <LogOut class="h-4 w-4" />
                  {{ t('auth.signOut') }}
                </button>
              </div>
            </Transition>
          </div>
        </div>
      </header>

      <!-- Page content -->
      <main class="flex-1 overflow-y-auto">
        <div class="p-6 max-w-screen-2xl mx-auto animate-fade-in">
          <router-view />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { formatNameWithTitle } from '@/utils/jobTitles'
import adminService from '@/services/adminService'
import LangToggle from '@/components/common/LangToggle.vue'
import logoMark from '@/assets/brand/logo-mark.svg'
import {
  Bell, User, Menu, ChevronRight, ChevronDown, ChevronLeft,
  Settings, LogOut, BarChart3, Users, Clock, Calendar, CalendarDays,
  UserCheck, AlarmClock, TrendingDown, Tag, Timer,
  DollarSign, Layers, Building2, FileText,
} from 'lucide-vue-next'

const { t } = useI18n()

const route     = useRoute()
const router    = useRouter()
const authStore = useAuthStore()

const sidebarOpen    = ref(false)
const collapsed      = ref(false)
const showProfileMenu = ref(false)

// ── Notifications ──────────────────────────────────────────────
const showNotifications   = ref(false)
const notifications       = ref([])
const unreadNotifications = ref(0)
let notifPollTimer = null

const fetchNotifications = async () => {
  try {
    const res = await adminService.getNotifications({ limit: 20 })
    const data = res?.data || {}
    notifications.value = (data.notifications || []).map(n => ({
      id: n.id,
      title: n.title,
      message: n.message,
      timestamp: n.createdAt,
      read: n.isRead,
    }))
    unreadNotifications.value = data.unreadCount || 0
  } catch (_) { /* keep last known state */ }
}

const toggleNotifications = () => {
  showNotifications.value = !showNotifications.value
  if (showNotifications.value) fetchNotifications()
}

const handleNotificationClick = (n) => {
  if (!n.read) {
    n.read = true
    unreadNotifications.value = Math.max(0, unreadNotifications.value - 1)
    adminService.markNotificationAsRead(n.id).catch(() => {})
  }
}

const markAllNotificationsRead = () => {
  notifications.value.forEach(n => { n.read = true })
  unreadNotifications.value = 0
  adminService.markAllNotificationsAsRead().catch(() => {})
}

const formatNotifTime = (ts) => {
  const d = Date.now() - new Date(ts)
  if (d < 60000)    return 'Just now'
  if (d < 3600000)  return `${Math.floor(d / 60000)}m ago`
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`
  return `${Math.floor(d / 86400000)}d ago`
}

onMounted(() => {
  fetchNotifications()
  notifPollTimer = setInterval(fetchNotifications, 60 * 1000)
})

onUnmounted(() => {
  if (notifPollTimer) clearInterval(notifPollTimer)
})

const currentUser  = computed(() => authStore.user)
const displayName  = computed(() =>
  formatNameWithTitle(currentUser.value?.name, currentUser.value?.job_title)
)
const userInitials = computed(() => {
  const name = currentUser.value?.name || ''
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'A'
})

const navigationGroups = computed(() => [
  {
    label: t('nav.overview'),
    items: [
      { name: t('nav.dashboard'), href: '/admin/dashboard', icon: BarChart3 },
    ],
  },
  {
    label: t('nav.workforce'),
    items: [
      { name: t('nav.employees'),  href: '/admin/employees',  icon: Users,     badge: null },
      { name: t('nav.attendance'), href: '/admin/attendance', icon: Clock },
      { name: t('nav.payroll'),    href: '/admin/payroll',    icon: DollarSign },
    ],
  },
  {
    label: t('nav.timeTracking'),
    items: [
      { name: t('nav.overtimeClock'),       href: '/admin/overtime-clock',      icon: Timer },
      { name: t('nav.shortLeaveSessions'),  href: '/admin/short-leave-sessions',icon: AlarmClock },
      { name: t('nav.lateArrivalDebts'),    href: '/admin/late-arrival-debts',  icon: TrendingDown },
    ],
  },
  {
    label: t('nav.leaveManagement'),
    items: [
      { name: t('nav.leaveManagementAdmin'), href: '/admin/leave-management', icon: Calendar },
      { name: t('nav.leaveBalances'),        href: '/admin/leave-balances',   icon: UserCheck },
      { name: t('nav.holidays'),             href: '/admin/holidays',         icon: CalendarDays },
    ],
  },
])

const configNavigation = computed(() => [
  { name: t('nav.departments'),  href: '/admin/departments',         icon: Building2 },
  { name: t('nav.leaveTypes'),   href: '/admin/leave-types',         icon: Tag },
  { name: t('nav.balanceBanks'), href: '/admin/balance-banks',       icon: Layers },
  { name: t('nav.workingHours'), href: '/admin/working-hours-config',icon: Settings },
])

const quickActions = computed(() => [
  { name: t('reports.title'),   action: 'reports',  icon: FileText },
  { name: t('settings.title'),  action: 'settings', icon: Settings },
])

const isActive = href => route.path.startsWith(href)

const currentPageTitle = computed(() => {
  const all = navigationGroups.value.flatMap(g => g.items).concat(configNavigation.value)
  return all.find(i => route.path.startsWith(i.href))?.name ?? t('nav.adminPortal')
})

const breadcrumbs = computed(() => {
  const segs = route.path.split('/').filter(Boolean)
  return segs.length <= 2 ? [] : segs.slice(1).map(s => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '))
})

const handleQuickAction = action => {
  showProfileMenu.value = false
  router.push(action === 'settings' ? '/admin/settings' : '/admin/reports')
}

const handleSettings = () => {
  showProfileMenu.value = false
  router.push('/admin/settings')
}

const handleLogout = async () => {
  try {
    await authStore.logout()
  } catch { /* ignore */ }
  window.location.href = '/login'
}

// Close dropdown when clicking outside (simple directive)
const vClickOutside = {
  mounted(el, binding) {
    el._clickOutside = e => { if (!el.contains(e.target)) binding.value(e) }
    document.addEventListener('click', el._clickOutside, true)
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutside, true)
  },
}
</script>
